import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import TitleRow from '@/components/ui/TitleRow'
import { useToggleLectureComplete } from '@/features/courses/api/mutations'
import { useSuspenseCourseDetail } from '@/features/courses/api/queries'
import { unlockAction } from '@/features/courses/lib/lecture-access'
import { coursePriceView } from '@/features/courses/lib/price'
import LectureBar from '@/screens/courses/components/player/LectureBar'
import LectureList from '@/screens/courses/components/player/LectureList'
import LectureNotes from '@/screens/courses/components/player/LectureNotes'
import { lecturePlace } from '@/screens/courses/components/player/lecture-sequence'
import {
  LectureBodySkeleton,
  LectureHeaderSkeleton,
} from '@/screens/courses/components/player/LectureSkeleton'
import LectureStage from '@/screens/courses/components/player/LectureStage'
import { PLAYER_GRID, STAGE_CELL } from '@/screens/courses/components/player/player-box'
import { loadLecture } from '@/screens/courses/LectureScreen.loader'
import { parseId } from '@/screens/courses/lib/params'

function LectureNotFound() {
  return (
    <ScreenLayout fullWidth>
      <EmptyState
        kind="not-found"
        size="page"
        headingLevel="h1"
        title="Lecture not found"
        description="This lecture may have been removed, or the link is wrong."
        action={
          <Link to="/courses" className={stateActionClass('page')}>
            Back to courses
          </Link>
        }
      />
    </ScreenLayout>
  )
}

/** The loaded page's own shape (`LectureSkeleton.tsx` has the numbers). */
function LecturePending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading this lecture…"
      header={<LectureHeaderSkeleton />}
      fullWidth
    >
      <LectureBodySkeleton />
    </ScreenLayout>
  )
}

/** Light on purpose (docs/route-loading.md, "Error components"). */
function LectureError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={<LectureHeaderSkeleton />}
      title="This lecture did not load"
    />
  )
}

export const Route = createFileRoute('/_app/courses/$courseId/lectures/$lectureId')({
  loader: loadLecture,
  pendingComponent: LecturePending,
  errorComponent: LectureError,
  component: LectureRoute,
  // The router's default remounts a route on any path-param change
  // (app/router.tsx). Here only the course counts: moving between lectures
  // keeps the screen — the list's open state on a phone, a "Mark complete"
  // still in flight (mm-be flips the mark, so a second press would undo
  // it), focus — and the page does not enter again for each lecture.
  remountDeps: ({ params }) => ({ courseId: params.courseId }),
})

/**
 * Unparseable ids never reach the suspense read (the loader skipped them
 * too). Keyed on the course, which the route's own `remountDeps` already
 * remounts on: nothing of one course may carry into another.
 */
function LectureRoute() {
  const params = Route.useParams()
  const courseId = parseId(params.courseId)
  const lectureId = parseId(params.lectureId)
  if (courseId === null || lectureId === null) return <LectureNotFound />
  return <LectureScreen key={courseId} courseId={courseId} lectureId={lectureId} />
}

/**
 * Learn · the lecture player: the lecture's video, its place in the course,
 * the member's "Mark complete", previous / next, the course's lecture list
 * and the lecture's notes (`components/player/player-box.ts` has the layout).
 *
 * Everything reads the course detail (mm-be has no single-lecture read), so
 * moving between lectures is a cache hit, and the tick "Mark complete" puts
 * in the cache shows in the list, the course page and back here at once.
 *
 * PROGRESS goes to mm-be only for a course the member owns — it answers 403
 * for anyone else, a free lecture included — so a free preview plays and
 * records nothing, and offers no "Mark complete". Positions are saved on a
 * throttle by the `<video>` (`use-watch-progress-saver.ts`); mm-be returns
 * no saved position, so a lecture always opens at its start.
 *
 * The player is keyed on the lecture: moving to another one unmounts this
 * one's player, whose saver sends its last position against ITS lecture
 * before the next player starts clean.
 */
function LectureScreen({ courseId, lectureId }: { courseId: number; lectureId: number }) {
  const { data: course } = useSuspenseCourseDetail(courseId)
  const toggleComplete = useToggleLectureComplete()

  const place = course === null ? null : lecturePlace(course.lectures, lectureId)
  if (course === null || place === null) return <LectureNotFound />
  const { lecture } = place
  // How the course is sold decides what a locked lecture says to do: the stage
  // and the list must agree with the course page's own button.
  const unlock = unlockAction(coursePriceView(course))

  // mm-be's endpoint FLIPS the mark, so a second request for the same
  // lecture before the first answers would undo it.
  const toggling = toggleComplete.isPending && toggleComplete.variables.lectureId === lecture.id

  // The hook's own `onError` toasts on every failure and rolls back only
  // this lecture, so the call site only needs the double-submit guard.
  const onToggleComplete = () => {
    if (toggling) return
    toggleComplete.mutate({ courseId, lectureId: lecture.id })
  }

  return (
    <ScreenLayout
      fullWidth
      // Back to the course: `TitleRow` takes a path, not params, and a Link
      // given none keeps the current ones — this page's `courseId`. The
      // course's title is the eyebrow, held to one line (some run past 80
      // characters, in caps) so the header keeps its skeleton's height.
      header={
        <TitleRow
          title={lecture.title}
          eyebrow={<span className="line-clamp-1">{course.title}</span>}
          back="/courses/$courseId"
        />
      }
    >
      <div className={PLAYER_GRID}>
        <div className={STAGE_CELL}>
          <LectureStage
            key={lecture.id}
            courseId={courseId}
            courseTitle={course.title}
            courseThumbnail={course.thumbnail}
            purchased={course.purchased}
            unlock={unlock}
            lecture={lecture}
          />
        </div>
        <LectureBar
          courseId={courseId}
          lecture={lecture}
          place={place}
          purchased={course.purchased}
          onToggleComplete={onToggleComplete}
        />
        <LectureList
          courseId={courseId}
          lectures={course.lectures}
          purchased={course.purchased}
          unlock={unlock}
          currentId={lecture.id}
        />
        <LectureNotes lecture={lecture} />
      </div>
    </ScreenLayout>
  )
}
